import React, { useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../@/components/ui/button";
import { EXAMPLE_MODELS } from "../data/exampleImages";
import { LEGO_COLORS } from "../constants/legoColors";
import { BUILD_SIZES, sizeForStuds } from "../constants/buildSizes";
import BrickShapeRenderer from "./BrickShapeRenderer/BrickShapeRenderer";
import { FIT_CAMERA, projectPoint } from "./BrickShapeRenderer/renderers";
import ModelMeshRenderer from "./ModelMeshRenderer/ModelMeshRenderer";
import { buildFromObjText } from "../utils/modelToLayers";
import { fitMeshToGrid, parseObjSafe } from "../utils/voxelize";

export interface ModelWorkbenchProps {
  objText: string | null;
  modelName: string | null;
  resolution: number;
  colorIndex: number;
  isProcessing: boolean;
  /** true once a build exists — the stage then shows the brick build */
  hasBuild?: boolean;
  /** true when a build exists but settings changed → "Rebuild" */
  pending: boolean;
  onObjFile: (objText: string, name: string) => void;
  onResolutionChange: (value: number) => void;
  onColorChange: (colorIndex: number) => void;
  onBuild: () => void;
}

const readFileAsText = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target?.result as string);
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsText(file);
  });

const CubeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="h-10 w-10" aria-hidden>
    <path d="M12 3 4.5 7.5v9L12 21l7.5-4.5v-9L12 3Z" />
    <path d="M4.5 7.5 12 12l7.5-4.5M12 12v9" />
  </svg>
);

// Default orbit for the preview — module scope so the reset callback stays
// referentially stable
const DEFAULT_ROT = { x: -28, y: 40 };

// Raw-clay tone for the original model (the bricks are the finished build).
// Mirrors the `clay` brand token in tailwind.config.js.
const MESH_COLOR = "#c9866b";

/**
 * The 3D stage — the page's hero. Empty state: a large drop target.
 * Loaded state: the live voxel preview at full size, orbitable and zoomable.
 * All state is owned by App (single-orchestrator pattern); the pipeline runs
 * there on the explicit Build trigger.
 */
export const ModelStage: React.FC<ModelWorkbenchProps> = ({
  objText,
  modelName,
  resolution,
  colorIndex,
  isProcessing,
  hasBuild = false,
  onObjFile,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = React.useState(false);

  // Orbit + zoom for the live preview — same UX as OptimizedBrickLayout
  const [rotation, setRotation] = React.useState({ ...DEFAULT_ROT });
  const [scale, setScale] = React.useState(1.0);
  const lastPointerRef = useRef<{ x: number; y: number } | null>(null);
  const orbitDraggingRef = React.useRef(false);

  const clamp = (v: number, lo: number, hi: number) =>
    Math.max(lo, Math.min(hi, v));

  const resetView = useCallback(() => {
    setRotation({ ...DEFAULT_ROT });
    setScale(1.0);
  }, []);

  const onOrbitPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.currentTarget.setPointerCapture(e.pointerId);
      orbitDraggingRef.current = true;
      lastPointerRef.current = { x: e.clientX, y: e.clientY };
    },
    [],
  );

  const onOrbitPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!orbitDraggingRef.current || !lastPointerRef.current) return;
      const last = lastPointerRef.current;
      const dx = e.clientX - last.x;
      const dy = e.clientY - last.y;
      lastPointerRef.current = { x: e.clientX, y: e.clientY };
      setRotation((prev) => ({
        x: clamp(prev.x - dy * 0.35, -72, 24),
        y: prev.y + dx * 0.45,
      }));
    },
    [],
  );

  const onOrbitPointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
      orbitDraggingRef.current = false;
      lastPointerRef.current = null;
    },
    [],
  );

  React.useEffect(() => {
    const onWinPointerUp = () => {
      orbitDraggingRef.current = false;
      lastPointerRef.current = null;
    };
    window.addEventListener("pointerup", onWinPointerUp);
    window.addEventListener("pointercancel", onWinPointerUp);
    return () => {
      window.removeEventListener("pointerup", onWinPointerUp);
      window.removeEventListener("pointercancel", onWinPointerUp);
    };
  }, []);

  const onOrbitWheel = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    e.preventDefault();
    setScale((prev) => clamp(prev + e.deltaY * -0.0012, 0.45, 3.2));
  }, []);

  const onOrbitKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const step = e.shiftKey ? 8 : 3;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        setRotation((r) => ({ ...r, y: r.y - step }));
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        setRotation((r) => ({ ...r, y: r.y + step }));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setRotation((r) => ({ ...r, x: clamp(r.x + step, -72, 24) }));
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setRotation((r) => ({ ...r, x: clamp(r.x - step, -72, 24) }));
      } else if (e.key === "+" || e.key === "=") {
        e.preventDefault();
        setScale((v) => clamp(v + 0.15, 0.45, 3.2));
      } else if (e.key === "-" || e.key === "_") {
        e.preventDefault();
        setScale((v) => clamp(v - 0.15, 0.45, 3.2));
      } else if (e.key === "0") {
        e.preventDefault();
        resetView();
      }
    },
    [resetView],
  );

  // Live 3D preview: the input voxelized at the full slider resolution
  // (debounced ~120ms so dragging the slider stays smooth).
  const [previewResolution, setPreviewResolution] = React.useState(resolution);
  React.useEffect(() => {
    const id = window.setTimeout(() => setPreviewResolution(resolution), 120);
    return () => window.clearTimeout(id);
  }, [resolution]);

  const preview = useMemo(() => {
    if (!objText) return null;
    try {
      return buildFromObjText(objText, {
        resolution: previewResolution,
        colorIndex,
      });
    } catch {
      return null;
    }
  }, [objText, previewResolution, colorIndex]);

  const previewBricks = useMemo(
    () => preview?.layers.flatMap((layer) => layer.bricks) ?? [],
    [preview],
  );

  // The original mesh, fitted to the same build grid as the bricks — both
  // views then share one camera, one scale and one frame.
  const fitted = useMemo(() => {
    if (!objText) return null;
    const mesh = parseObjSafe(objText);
    if (!mesh) return null;
    try {
      return fitMeshToGrid(mesh, previewResolution);
    } catch {
      return null;
    }
  }, [objText, previewResolution]);

  // Which view the stage shows: the original model first, the brick build
  // one toggle away — and once a build exists, the build is what the stage
  // is for, so it takes over
  const [view, setView] = React.useState<"original" | "bricks">("original");
  React.useEffect(() => {
    setView(hasBuild ? "bricks" : "original");
  }, [hasBuild]);

  // Fit the model into the hero canvas: project its bounding box with the
  // reference camera (stable while orbiting) and scale it to the canvas.
  const previewPixelSize = useMemo(() => {
    if (!fitted) return 8;
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    for (const x of [0, fitted.sizeX]) {
      for (const y of [0, fitted.sizeY]) {
        for (const z of [0, fitted.sizeZ]) {
          const p = projectPoint(FIT_CAMERA, x, y, z);
          minX = Math.min(minX, p.x);
          maxX = Math.max(maxX, p.x);
          minY = Math.min(minY, p.y);
          maxY = Math.max(maxY, p.y);
        }
      }
    }
    const xSpan = Math.max(1, maxX - minX);
    const ySpan = Math.max(1, maxY - minY);
    return Math.max(3, Math.floor(Math.min(720 / xSpan, 360 / ySpan)));
  }, [fitted]);

  const handleFiles = useCallback(
    async (files: FileList | null) => {
      const file = files?.[0];
      if (!file || !file.name.toLowerCase().endsWith(".obj")) return;
      onObjFile(await readFileAsText(file), file.name);
    },
    [onObjFile],
  );

  const onDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);
      void handleFiles(e.dataTransfer.files);
    },
    [handleFiles],
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        fileInputRef.current?.click();
      }
    },
    [],
  );

  // Examples are stored by id — show the human label
  const displayName =
    EXAMPLE_MODELS.find((m) => m.id === modelName)?.label ?? modelName;

  const examples = (
    <div className="mt-5">
      <span className="eyebrow" id="model-example-label">
        {t("uploader.tryExample")}
      </span>
      <div
        className="mt-2 grid grid-cols-2 sm:grid-cols-5 gap-2"
        role="group"
        aria-labelledby="model-example-label"
      >
        {EXAMPLE_MODELS.map((ex) => (
          <button
            key={ex.id}
            type="button"
            title={ex.label}
            onClick={() => {
              fetch(`${window.location.origin}${ex.path}`)
                .then((res) => res.text())
                .then((text) => onObjFile(text, ex.id));
            }}
            className="flex w-full items-center gap-2 rounded-full border-[1.5px] border-ink bg-surface py-1 pl-1 pr-3 text-sm font-semibold text-ink shadow-hard-sm transition duration-150 ease-out-expo motion-reduce:transition-none hover:text-brick hover:border-brick focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brick active:translate-x-0.5 active:translate-y-0.5 active:shadow-hard-press"
          >
            <span
              aria-hidden
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-ink/20 text-brick"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                <path d="M12 3 4.5 7.5v9L12 21l7.5-4.5v-9L12 3Z" />
                <path d="M4.5 7.5 12 12l7.5-4.5M12 12v9" />
              </svg>
            </span>
            <span>{ex.label}</span>
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="w-full">
      <input
        ref={fileInputRef}
        type="file"
        accept=".obj"
        data-testid="model-file-input"
        onChange={(e) => void handleFiles(e.target.files)}
        className="hidden"
      />

      {preview && previewBricks.length > 0 ? (
        /* Loaded: the preview IS the hero */
        <div className="panel p-4 sm:p-6" data-testid="model-preview">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-3 min-w-0">
              <span className="font-display font-extrabold text-lg text-ink truncate">
                {displayName}
              </span>
              <span className="spec-chip text-ink tabular-nums">
                {preview.width} × {preview.height} ·{" "}
                {t("model.layersShort", { count: preview.layers.length })}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {/* View toggle — original model first, bricks one click away */}
              <div
                className="flex items-center gap-1 rounded-full border-[1.5px] border-ink bg-surface p-1"
                role="group"
                aria-label={t("model.viewLabel")}
              >
                {(
                  [
                    ["original", t("model.viewOriginal")],
                    ["bricks", t("model.viewBricks")],
                  ] as const
                ).map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    aria-pressed={view === key}
                    onClick={() => setView(key)}
                    className={`rounded-full px-3 py-1 text-xs font-semibold transition duration-150 ease-out-expo motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brick ${
                      view === key
                        ? "bg-brick text-surface"
                        : "text-ink-muted hover:text-brick"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                className="btn-press"
              >
                {t("uploader.chooseFile")}
              </Button>
            </div>
          </div>

          <div
            role="img"
            aria-label={t("model.previewHint")}
            tabIndex={0}
            onPointerDown={onOrbitPointerDown}
            onPointerMove={onOrbitPointerMove}
            onPointerUp={onOrbitPointerUp}
            onWheel={onOrbitWheel}
            onKeyDown={onOrbitKeyDown}
            className="flex justify-center panel-inset py-4 cursor-grab active:cursor-grabbing focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brick touch-none select-none"
          >
            {view === "original" && fitted ? (
              <ModelMeshRenderer
                mesh={fitted.mesh}
                canvasSize={{ width: 760, height: 400 }}
                pixelSize={previewPixelSize}
                rotation={rotation}
                scale={scale}
                color={MESH_COLOR}
              />
            ) : (
              <BrickShapeRenderer
                bricks={previewBricks}
                canvasSize={{ width: 760, height: 400 }}
                pixelSize={previewPixelSize}
                is3D
                rotation={rotation}
                scale={scale}
              />
            )}
          </div>

          <div className="mt-3 flex items-center justify-between gap-3">
            <p className="text-xs text-ink-muted leading-relaxed">
              {t("model.previewHint")}
            </p>
            <button
              type="button"
              onClick={resetView}
              className="shrink-0 rounded-full border-[1.5px] border-ink bg-surface px-3 py-1 text-xs font-semibold text-ink shadow-hard-sm transition duration-150 ease-out-expo motion-reduce:transition-none hover:text-brick hover:border-brick focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brick active:translate-x-0.5 active:translate-y-0.5 active:shadow-hard-press"
            >
              {t("common.reset")}
            </button>
          </div>
        </div>
      ) : (
        /* Empty: a large drop target is the hero */
        <div
          tabIndex={0}
          aria-label={t("model.dropTitle")}
          aria-busy={isProcessing}
          className={`upload-dropzone relative w-full px-6 py-10 sm:py-14 bg-surface border-2 border-ink/90 rounded-2xl transition duration-200 ease-out-expo cursor-pointer motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brick ${
            isDragging
              ? "bg-signal-soft/70 border-brick translate-x-[-2px] translate-y-[-2px] shadow-hard-drag"
              : "shadow-hard hover:translate-x-[-1px] hover:translate-y-[-1px] hover:shadow-hard-hover active:translate-x-[2px] active:translate-y-[2px] active:shadow-hard-press"
          }`}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragEnter={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            setIsDragging(false);
          }}
          onDrop={onDrop}
          onClick={() => fileInputRef.current?.click()}
          onKeyDown={onKeyDown}
        >
          <div className="flex flex-col items-center justify-center space-y-3">
            <div className="text-ink-muted" aria-hidden>
              <CubeIcon />
            </div>
            <p className="text-ink font-display font-extrabold text-xl sm:text-2xl tracking-tight max-w-lg text-center leading-snug">
              {t("model.dropTitle")}
            </p>
            <p className="text-ink-muted text-sm text-center max-w-md">
              {t("model.dropHint")}
            </p>
            <Button
              onClick={(e) => {
                e.stopPropagation();
                fileInputRef.current?.click();
              }}
              className="btn-press gap-2 mt-2"
            >
              <span>{t("uploader.chooseFile")}</span>
            </Button>
          </div>
        </div>
      )}

      {/* The examples are the way in, and the quick swap once a model is
          loaded */}
      {examples}
    </div>
  );
};

/**
 * 3D-model controls: build size, brick color, build trigger. Rendered as a
 * rail under the stage (desktop); stacks below it on mobile.
 */
export const ModelSettings: React.FC<ModelWorkbenchProps> = ({
  objText,
  resolution,
  colorIndex,
  isProcessing,
  pending,
  onResolutionChange,
  onColorChange,
  onBuild,
}) => {
  const { t } = useTranslation();
  const activeSize = sizeForStuds(resolution);

  return (
    <div className="panel panel-hover p-6">
      {/* Build size */}
      <div className="flex items-baseline justify-between gap-4 mb-5">
        <label htmlFor="model-resolution-slider" className="eyebrow">
          {t("model.resolution")}
        </label>
        <span
          className="text-4xl font-extrabold text-brick tabular-nums leading-none"
          aria-hidden
        >
          {resolution}
          <span className="text-ink-muted text-base font-semibold ml-1">
            {t("model.studs")}
          </span>
        </span>
      </div>
      <input
        id="model-resolution-slider"
        type="range"
        min="8"
        max="48"
        step="2"
        value={resolution}
        onChange={(e) => onResolutionChange(Number(e.target.value))}
        className="w-full h-2 rounded-full bg-canvas accent-brick cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brick"
      />
      <div className="flex justify-between mt-2 text-xs font-semibold text-ink-muted">
        <span>{t("model.hintFaster")}</span>
        <span>{t("model.hintDetail")}</span>
      </div>

      {/* Size presets — a clothing scale over the same slider */}
      <div
        className="mt-4 flex flex-wrap gap-2"
        role="group"
        aria-label={t("model.size")}
      >
        {BUILD_SIZES.map((size) => (
          <button
            key={size.id}
            type="button"
            aria-pressed={activeSize.id === size.id}
            title={`${size.label} · ${size.studs}`}
            onClick={() => onResolutionChange(size.studs)}
            className={`rounded-full border-[1.5px] px-3 py-1 text-sm font-semibold shadow-hard-sm transition duration-150 ease-out-expo motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brick active:translate-x-0.5 active:translate-y-0.5 active:shadow-hard-press ${
              activeSize.id === size.id
                ? "border-brick bg-brick text-surface"
                : "border-ink bg-surface text-ink hover:text-brick hover:border-brick"
            }`}
          >
            {size.label}
          </button>
        ))}
      </div>

      {/* Brick color */}
      <div className="mt-6 pt-5 border-t border-ink/10">
        <span className="eyebrow text-ink-muted" id="model-color-label">
          {t("model.color")}
        </span>
        <div
          className="mt-3 flex flex-wrap gap-2"
          role="group"
          aria-labelledby="model-color-label"
        >
          {LEGO_COLORS.map((color, index) => (
            <button
              key={color.name}
              type="button"
              aria-pressed={colorIndex === index}
              aria-label={color.name}
              title={color.name}
              onClick={() => onColorChange(index)}
              className={`h-8 w-8 rounded-full border-2 transition duration-150 ease-out-expo motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brick active:translate-y-px ${
                colorIndex === index
                  ? "border-brick ring-2 ring-brick ring-offset-1 ring-offset-surface scale-110"
                  : "border-ink/30 hover:scale-105"
              }`}
              style={{ backgroundColor: `rgb(${color.r}, ${color.g}, ${color.b})` }}
            />
          ))}
        </div>
      </div>

      {/* Build */}
      <Button
        onClick={onBuild}
        disabled={isProcessing || !objText}
        className="btn-press w-full mt-6"
      >
        {isProcessing
          ? t("common.processing")
          : pending
            ? t("model.rebuild")
            : t("model.build")}
      </Button>
      {!objText && (
        <p className="mt-3 text-xs text-ink-muted text-center">
          {t("model.buildHint")}
        </p>
      )}
    </div>
  );
};
